import { decodeWithTE } from "app/common/core/decode"
import { decodeWith } from "app/common/core/decode"
import { constZero } from "app/common/utilities/contstants"
import { dba } from "app/common/utilities/db"
import { omit } from "app/common/utilities/omit"
import { ArticlePathsDC } from "app/data/articles/article.types"
import { ArticleDC } from "app/data/articles/article.types"
import { articlePathsArgs, getArticleBySlugArgs } from "app/data/articles/article.validators"
import { ArticleFindManyVX } from "app/data/articles/article.validators"
import { findArticles } from "app/data/articles/findArticles"
import { findFirstArticle } from "app/data/articles/findFirstArticle"
import { toArticleMap } from "app/models/article.map"
import addDays from "date-fns/addDays"
import db, { LocaleD, Prisma } from "db"
import { array, either, number, option, ord, readonlyArray, taskEither } from "fp-ts"
import { flow, pipe } from "fp-ts/function"

export const getArticleBySlug = flow(getArticleBySlugArgs, findFirstArticle)

export const getArticlePaths = () =>
  pipe(
    articlePathsArgs,
    findArticles,
    taskEither.chain(taskEither.traverseArray(decodeWithTE(ArticlePathsDC)))
  )

interface GetArticleListArgs {
  take?: number
  where?: Prisma.ArticleWhereInput | undefined
}

const getArticleList = ({ where = undefined, take = undefined }: GetArticleListArgs) =>
  findArticles({
    ...ArticleFindManyVX(where),
    take,
  })

interface GetRecentArticles {
  articleId?: number
  locale: LocaleD
  take?: number
}

export const getRecentArticles = ({ locale, articleId, take = undefined }: GetRecentArticles) =>
  pipe(
    getArticleList({ take, where: { id: { not: articleId } } }),
    taskEither.chainEitherK(
      either.traverseArray(
        flow(decodeWith(ArticleDC), either.map(flow(toArticleMap, (map) => map[locale])))
      )
    )
  )

interface GetPopularArticlesArgs {
  articleId?: number
  inPastNDays?: number
  locale: LocaleD
  maxResults?: number
}

const byViewCount = ord.contramap((article: { viewCount: number }) => article.viewCount)(number.Ord)

const byViewCountDesc = ord.reverse(byViewCount)

export const getPopularArticles = ({
  articleId,
  inPastNDays,
  locale,
  maxResults = 5,
}: GetPopularArticlesArgs) =>
  pipe(
    dba(() =>
      db.articlePageView.groupBy({
        _count: {
          articleId: true,
        },
        by: ["articleId"],
        orderBy: {
          _count: {
            articleId: "desc",
          },
        },
        skip: 0,
        take: maxResults,
        where: {
          ...pipe(
            inPastNDays,
            option.fromNullable,
            option.map((inPastNDays) => ({
              createdAt: {
                gte: addDays(new Date(), -inPastNDays),
              },
            })),
            option.toUndefined
          ),
          page: {
            NOT: { articleId },
          },
        },
      })
    ),
    taskEither.bindTo("aggregate"),
    taskEither.bind("articles", ({ aggregate }) =>
      getArticleList({
        where: {
          pages: {
            some: {
              id: {
                in: pipe(
                  aggregate,
                  array.map((a) => a.articleId)
                ),
              },
            },
          },
        },
      })
    ),
    taskEither.map(({ aggregate, articles }) =>
      pipe(
        articles,
        array.map(
          flow(decodeWith(ArticleDC), either.map(flow(toArticleMap, (map) => map[locale])))
        ),
        array.rights,
        array.map((article) => ({
          ...article,
          viewCount: pipe(
            aggregate,
            array.findFirst((a) => a.articleId === article.id),
            option.map((a) => a._count.articleId),
            option.getOrElse(constZero)
          ),
        }))
      )
    ),
    taskEither.map(array.sort(byViewCountDesc)),
    taskEither.map(array.map((a) => omit(a, "viewCount"))),
    taskEither.map(readonlyArray.fromArray)
  )
